/**
 * Notification sound for new chat messages.
 *
 * Uses the Web Audio API to synthesize a short, pleasant two-tone "ding"
 * so we don't need to ship any audio asset (and don't risk 404 routing
 * through K8s ingress for static files).
 *
 * Features:
 *  - Throttled (max 1 ding per ~800 ms) so a burst of inbound messages
 *    doesn't drown the user.
 *  - Honours a per-browser mute preference at localStorage["chat:notif:muted"].
 *  - Honours `document.hidden` only as a *boost* (slightly louder when the
 *    tab is in the background) — the sound still plays when focused.
 *  - Safe to call from anywhere; silently no-ops on SSR / unsupported envs.
 */

const STORAGE_KEY = "chat:notif:muted";
const MIN_INTERVAL_MS = 800;

let _ctx = null;
let _lastPlayed = 0;
let _unlocked = false;

function getCtx() {
    if (typeof window === "undefined") return null;
    if (_ctx) return _ctx;
    try {
        const Ctor = window.AudioContext || window.webkitAudioContext;
        if (!Ctor) return null;
        _ctx = new Ctor();
    } catch {
        return null;
    }
    return _ctx;
}

/**
 * Browsers block AudioContext until a user gesture. Call this once after
 * any click / keydown to "unlock" the context so subsequent dings play.
 */
export function unlockNotificationSound() {
    if (_unlocked) return;
    const ctx = getCtx();
    if (!ctx) return;
    if (ctx.state === "suspended") {
        ctx.resume?.().catch(() => {});
    }
    _unlocked = true;
}

export function isNotificationSoundMuted() {
    if (typeof window === "undefined") return false;
    try {
        return window.localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
        return false;
    }
}

export function setNotificationSoundMuted(muted) {
    if (typeof window === "undefined") return;
    try {
        if (muted) window.localStorage.setItem(STORAGE_KEY, "1");
        else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * Play a short notification sound. Returns true if it actually played.
 */
export function playNotificationSound() {
    if (typeof window === "undefined") return false;
    if (isNotificationSoundMuted()) return false;
    const now = Date.now();
    if (now - _lastPlayed < MIN_INTERVAL_MS) return false;

    const ctx = getCtx();
    if (!ctx) return false;

    // Browsers may have suspended the context — try to resume it.
    if (ctx.state === "suspended") {
        ctx.resume?.().catch(() => {});
    }

    try {
        const t0 = ctx.currentTime;
        // Slightly louder when the tab is hidden so the user notices it.
        const peak =
            typeof document !== "undefined" && document.hidden ? 0.22 : 0.16;

        const playTone = (freq, start, dur) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(freq, t0 + start);
            // Quick attack + exponential decay = clean "ding"
            gain.gain.setValueAtTime(0.0001, t0 + start);
            gain.gain.exponentialRampToValueAtTime(peak, t0 + start + 0.01);
            gain.gain.exponentialRampToValueAtTime(0.0001, t0 + start + dur);
            osc.connect(gain).connect(ctx.destination);
            osc.start(t0 + start);
            osc.stop(t0 + start + dur + 0.02);
        };

        // Two-tone: high then a perfect-fifth above ⇒ pleasant chime.
        playTone(880, 0, 0.18); // A5
        playTone(1318.5, 0.09, 0.22); // E6
        _lastPlayed = now;
        // Surface a timestamp on `window` so tests / observers can confirm
        // a chat ding actually fired without intercepting the AudioContext.
        try {
            if (typeof window !== "undefined") {
                window.__lastChatSoundAt = now;
            }
        } catch {
            /* ignore */
        }
        return true;
    } catch {
        return false;
    }
}
